import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import * as _ from 'lodash';
import { Observable, of, Subject } from 'rxjs';
import { debounceTime, delay, takeUntil, tap } from 'rxjs/operators';

import { FastenerBasePointHelper } from '../../../../../helpers/fastener-base-point.helper';
import { FastenerBasePointEnum } from '../../../../../models/cutting/cutting-custom-hole.model';
import { Position } from '../../../../../models/cutting/cutting-svg.model';
import { CustomHoleCoordinates } from '../../fastener-base-point-select.component';

export type TablePlateCoordCol = 'x' | 'y';

@Component({
  selector: 'csl-plate-hole-coordinates-form',
  templateUrl: './plate-hole-coordinates-form.component.html',
  styleUrls: ['./plate-hole-coordinates-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class PlateHoleCoordinatesFormComponent implements OnInit, OnDestroy {
  fieldWithErrors: Set<string> = new Set();
  lockedValue: { row: number; value: number; col: TablePlateCoordCol } = undefined;
  private formUpdated: Subject<CustomHoleCoordinates[]> = new Subject();
  unsubscribe$: Subject<void> = new Subject();

  @Input() coords: CustomHoleCoordinates[] = [];
  @Input() panelHeight = 0;
  @Input() panelWidth = 0;
  @Input() selectedMultiSide: Position;
  @Input() point: FastenerBasePointEnum;
  @Input() side: Position | undefined;

  @Output() formChanged: EventEmitter<CustomHoleCoordinates[]> = new EventEmitter();

  constructor(private ref: ChangeDetectorRef) {
    this.formUpdated
      .asObservable()
      .pipe(debounceTime(500))
      .subscribe(response => {
        this.formChanged.emit(this.fieldWithErrors.size === 0 ? this.coords : undefined);
      });
  }

  ngOnInit(): void {
    document.onkeyup = (e: KeyboardEvent): void => {
      // tslint:disable-next-line: deprecation
      if (e.keyCode === 45 || e.keyCode === 40) {
        this.addCoordAndFocusLast();
      }
    };

    this.addCoordAndFocusLast();
  }

  valueUpdated(): void {
    this.formUpdated.next(null);
  }

  toggleLock(row: number, value: number, col: TablePlateCoordCol): void {
    if (this.isLocked(row, value, col)) {
      this.lockedValue = undefined;
    } else {
      this.lockedValue = { row, value, col };
    }
  }

  isLocked(row: number, value: number | null, col: TablePlateCoordCol): boolean {
    if (value !== null) {
      return (
        this.lockedValue &&
        this.lockedValue.row === row &&
        this.lockedValue.value === value &&
        this.lockedValue.col === col
      );
    } else {
      return false;
    }
  }

  fastenerPoint2Position(item: CustomHoleCoordinates): Position {
    return this.getBasePointAngleName(item.multiSide, item.point);
  }

  setNewPosition(pos: Position, item: CustomHoleCoordinates): void {
    const setRes = FastenerBasePointHelper.setBasePoint(pos, undefined);

    if (!_.isUndefined(setRes.selectedPoint)) {
      item.point = setRes.selectedPoint;
    }

    if (!_.isUndefined(setRes.selectedSide)) {
      item.multiSide = setRes.selectedSide;
    }

    this.valueUpdated();
  }

  checkErrors(errors: any, field: TablePlateCoordCol, rowIndex: number): void {
    const fieldId = field + rowIndex;

    if (!errors) {
      this.fieldWithErrors.delete(fieldId);
      return;
    }
    this.fieldWithErrors.add(fieldId);
  }

  getError(errors: any): Observable<string> {
    if (!errors) {
      return of('');
    }

    if (errors.range) {
      return of($localize`Число должно находиться в диапазоне от ${errors.range.value[0]} до ${errors.range.value[1]}`);
    }

    if (errors.required) {
      return of($localize`Поле не должно быть пустым`);
    }

    if (errors.number) {
      return of($localize`Необходимо ввести число`);
    }
  }

  getBasePointAngleName(side: Position, basePoint: FastenerBasePointEnum): Position {
    switch (side) {
      case 'tc':
        return basePoint === FastenerBasePointEnum.Start ? 'tl' : 'tr';

      case 'rc':
        return basePoint === FastenerBasePointEnum.Start ? 'tr' : 'br';

      case 'bc':
        return basePoint === FastenerBasePointEnum.Start ? 'br' : 'bl';

      case 'lc':
        return basePoint === FastenerBasePointEnum.Start ? 'bl' : 'tl';
    }
  }

  getDisabledPoints(): Position[] {
    const res: Position[] = ['c'];

    switch (this.side) {
      case 'tc':
        res.push('bl', 'br');
        break;
      case 'rc':
        res.push('tl', 'bl');
        break;
      case 'bc':
        res.push('tl', 'tr');
        break;
      case 'lc':
        res.push('tr', 'br');
        break;
    }

    return res;
  }

  addCoord(): void {
    this.coords.push({
      x: this.lockedValue && this.lockedValue.col === 'x' ? this.lockedValue.value : 0,
      y: this.lockedValue && this.lockedValue.col === 'y' ? this.lockedValue.value : 0,
      z: 0,
      point: this.point,
      multiSide: this.selectedMultiSide,
    });
    this.formChanged.emit(this.fieldWithErrors.size === 0 && this.coords.length > 0 ? this.coords : undefined);
  }

  addCoordAndFocusLast(): void {
    of(this.addCoord())
      .pipe(
        takeUntil(this.unsubscribe$),
        delay(50),
        tap(() => this.focusLastCoord())
      )
      .subscribe();
  }

  private focusLastCoord(): void {
    const inputs: NodeListOf<HTMLInputElement> = document.querySelectorAll(
      '#plate-hole-coordinates-form table .coord-row .x-col input'
    );
    if (inputs.length > 0) {
      inputs[inputs.length - 1].focus();
      inputs[inputs.length - 1].select();
    }
  }

  deleteCoord(coord: CustomHoleCoordinates, rowIndex: number): void {
    const coordIndex = this.coords.indexOf(coord);

    if (coordIndex !== -1) {
      this.coords.splice(coordIndex, 1);

      if (this.lockedValue && this.lockedValue.row === rowIndex) {
        this.lockedValue = undefined;
      }

      this.fieldWithErrors.delete(('x' as TablePlateCoordCol) + rowIndex);
      this.fieldWithErrors.delete(('y' as TablePlateCoordCol) + rowIndex);

      this.formChanged.emit(this.fieldWithErrors.size === 0 && this.coords.length > 0 ? this.coords : undefined);
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
  }
}
